"use client"

import { useState, useEffect } from "react"
import { Menu, X, Phone } from "lucide-react"
import { Button } from "@/components/ui/button"
import Image from "next/image"
import Link from "next/link"
import { Locale } from "@/lib/i18n-config"
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu"
import { whatsappUrl } from "@/lib/contact"

// CSS for flash effect
const flashStyle = `
  @keyframes flashLayout {
    0% { opacity: 1; }
    50% { opacity: 0.3; }
    100% { opacity: 1; }
  }
  .flash-layout {
    animation: flashLayout 0.4s ease-in-out;
  }
`

interface HeaderProps {
  lang: Locale
  dict: {
    nav: {
      inicio: string
      "quem-somos": string
      servicos: string
      portfolio: string
      processo: string
      sistema: string
      clientes: string
      depoimentos: string
    }
    cta: string
  }
}

export function Header({ lang, dict }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [showPortfolio, setShowPortfolio] = useState(true)
  const [showTestimonials, setShowTestimonials] = useState(true)
  const [flashing, setFlashing] = useState(false)

  useEffect(() => {
    setShowPortfolio(process.env.NEXT_PUBLIC_HIDE_PORTFOLIO !== 'true')
    setShowTestimonials(process.env.NEXT_PUBLIC_HIDE_TESTIMONIALS !== 'true')
  }, [])

  const navLinks = [
    { label: dict.nav.inicio, href: "#inicio" },
    { label: dict.nav["quem-somos"], href: "#quem-somos" },
    { label: dict.nav.servicos, href: "#servicos" },
    ...(showPortfolio ? [{ label: dict.nav.portfolio, href: "#portfolio" }] : []),
    { label: dict.nav.processo, href: "#como-funciona" },
    { label: dict.nav.sistema, href: "#sistema" },
    ...(showTestimonials ? [{ label: dict.nav.depoimentos, href: "#depoimentos" }] : []),
  ]

  const languages = [
    { code: 'pt', label: 'PT', flag: '🇧🇷' },
    { code: 'en', label: 'EN', flag: '🇺🇸' },
    { code: 'es', label: 'ES', flag: '🇪🇸' },
  ]

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40)
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  const handleLanguageChange = () => {
    setFlashing(true)
    setTimeout(() => setFlashing(false), 400)
  }

  return (
    <>
      <style>{flashStyle}</style>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "glass-premium border-b border-border/40 py-2"
            : "bg-transparent py-4"
        } ${flashing ? "flash-layout" : ""}`}
      >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 lg:px-6">
        {/* Logo */}
        <Link href={`/${lang}`} className="relative group transition-transform duration-300 hover:scale-105 active:scale-95">
          <Image
            src="/images/lughar-logo.svg"
            alt="Lughar d'Buzios — Estilo de Viver"
            width={180}
            height={50}
            className={`h-9 w-auto transition-all duration-500 lg:h-11 ${
              scrolled ? "" : "brightness-0 invert opacity-90"
            }`}
            priority
          />
        </Link>

        {/* Navigation */}
        <nav className="hidden items-center lg:gap-3 xl:gap-6 lg:flex" aria-label="Navegacao principal">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              aria-label={link.label}
              className={`relative py-2 text-[13px] font-bold uppercase tracking-tight transition-all duration-300 group ${
                scrolled
                  ? "text-muted-foreground hover:text-primary"
                  : "text-primary-foreground/70 hover:text-primary-foreground"
              }`}
            >
              {link.label}
              <span className="absolute bottom-0 left-0 h-0.5 w-0 bg-accent transition-all duration-300 group-hover:w-full" />
            </a>
          ))}
        </nav>

        {/* Desktop Controls */}
        <div className="hidden items-center gap-4 lg:flex">
          {/* Language Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                onClick={handleLanguageChange}
                className={`flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[11px] font-black tracking-widest transition-all ${
                  scrolled ? "bg-secondary text-primary hover:bg-secondary/80" : "bg-white/10 text-white hover:bg-white/20"
                }`}
              >
                <span className="text-lg">
                  {languages.find(l => l.code === lang)?.flag || '🌐'}
                </span>
                {lang.toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass-premium border-border/40">
              {languages.map((l) => (
                <DropdownMenuItem key={l.code} asChild>
                  <Link 
                    href={`/${l.code}`} 
                    onClick={handleLanguageChange}
                    className={`flex items-center gap-3 px-4 py-2 text-xs font-bold transition-colors hover:text-accent ${lang === l.code ? 'text-accent' : ''}`}
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <a
            href={whatsappUrl('Olá! Gostaria de solicitar um orçamento.')}
            target="_blank"
            rel="noopener noreferrer"
            className="transition-transform duration-300 hover:scale-105 active:scale-95"
          >
            <Button size="sm" className="bg-accent text-accent-foreground hover:bg-accent/90 px-6 font-black uppercase tracking-widest shadow-lg shadow-accent/20">
              <Phone className="mr-2 h-3.5 w-3.5" />
              {dict.cta}
            </Button>
          </a>
        </div>

        {/* Mobile Controls */}
        <div className="flex items-center gap-4 lg:hidden">
           {/* Mobile Lang Button (Simplified) */}
           <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                onClick={handleLanguageChange}
                aria-label="Selecionar idioma" 
                className={`p-2 rounded-full transition-all text-xl ${scrolled ? 'text-foreground' : 'text-primary-foreground'}`}
              >
                {languages.find(l => l.code === lang)?.flag || '🌐'}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="glass-premium border-border/40">
              {languages.map((l) => (
                <DropdownMenuItem key={l.code} asChild>
                  <Link 
                    href={`/${l.code}`} 
                    onClick={handleLanguageChange}
                    className="flex items-center gap-3 px-4 py-2 text-xs font-bold"
                  >
                    <span>{l.flag}</span>
                    <span>{l.label}</span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <button
            className="rounded-full p-2 transition-colors hover:bg-white/10"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Fechar menu" : "Abrir menu"}
          >
            {mobileOpen ? (
              <X className="h-6 w-6 text-foreground" />
            ) : (
              <Menu
                className={`h-6 w-6 transition-colors ${
                  scrolled ? "text-foreground" : "text-primary-foreground"
                }`}
              />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="border-t border-border bg-card/98 backdrop-blur-md lg:hidden">
          <nav className="mx-auto flex max-w-7xl flex-col gap-1 px-4 py-4" aria-label="Menu mobile">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-lg px-4 py-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary"
              >
                {link.label}
              </a>
            ))}
            <a
              href={whatsappUrl('Olá! Gostaria de solicitar um orçamento.')}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2"
            >
              <Button className="w-full bg-accent text-accent-foreground hover:bg-accent/90 gap-2 font-bold">
                <Phone className="h-4 w-4" />
                {dict.cta}
              </Button>
            </a>
          </nav>
        </div>
      )}
      </header>
    </>
  )
}
